import { isPlatformBrowser } from '@angular/common';
import { inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Route, Router, Routes } from '@angular/router';
import { PageFullUrls, PageRoute } from 'ctt-babylon';
import { distinctUntilChanged, filter, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { SSR_URL } from '../../tokens/ssr-url.token';
import { SiteConfigService } from '../site-config/site-config.service';
import { UserInfoService } from '../user/user-info.service';
import { TEMPLATES } from './templates';

@Injectable({
    providedIn: 'root',
})
export class RoutesService {
    private readonly ssrUrl = inject(SSR_URL, { optional: true });
    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: Object,
        private readonly siteSvc: SiteConfigService,
        private readonly router: Router,
        private readonly userInfoService: UserInfoService
    ) {
        this.platformId = platformId;
    }

    homeId!: string | number;

    modelId = 0;
    currentPageId = 0;

    private pages: any[] = [];

    addRoutingEvents(): void {
        this.router.events
            .pipe(
                filter(
                    (event): event is NavigationEnd =>
                        event instanceof NavigationEnd
                ),

                distinctUntilChanged(
                    (prev, curr) =>
                        prev.urlAfterRedirects === curr.urlAfterRedirects
                ),

                tap((event: NavigationEnd) => {
                    this.userInfoService.updateVisitData();
                })
            )
            .subscribe();
    }

    setCurrenPageId(pageId: string | number): void {
        this.currentPageId = Number(pageId);
    }

    private toPath(url?: string): string {
        return (url ?? '')
            .trim()
            .replace(/^\/+|\/+$/g, '')
            .toLowerCase();
    }

    private toRedirect(url?: string): string {
        return this.toPath(url);
    }

    private toAbs(url?: string): string {
        const p = this.toPath(url);
        return p ? '/' + p : '/';
    }

    setSettingsRouter(pages: PageFullUrls[]) {
        const pagesFiltered = this.filterPagesAndRoutes(pages);

        if (pagesFiltered) {
            // 1. Configurar Home ID
            this.homeId = pagesFiltered.find(
                (page) =>
                    page.name?.toLowerCase() === 'home' ||
                    page.name?.toLowerCase() === '_home'
            )?.pageId!;

            const homePage = pagesFiltered.find((p) => p.id === this.homeId);

            // 2. Calcular URL de Home Base
            const langRoute = homePage?.route?.find(
                (r) =>
                    r?.languageId === this.siteSvc.selectedLanguageInstant?.id
            );
            const defaultLang =
                this.siteSvc.selectedLanguageInstant?.locate || 'es';
            const targetHomeUrl = langRoute?.url || defaultLang;
            const rootRedirect = this.toRedirect(targetHomeUrl);

            // 3. Rutas Básicas
            const basicRoutes: Routes = [
                {
                    path: '',
                    redirectTo: rootRedirect,
                    pathMatch: 'full',
                },
                {
                    path: '**',
                    pathMatch: 'full',
                    loadComponent: () =>
                        import('../../pages/404/404.component').then(
                            (m) => m.ErrorComponent
                        ),
                },
            ];

            // 4. Mapeo de páginas
            const pagesMaped = pages
                .filter(
                    (page): page is PageFullUrls =>
                        page.id !== null &&
                        page !== null &&
                        page.route !== null &&
                        page.route !== undefined
                )
                .flatMap((page) =>
                    (page.route || []).flatMap((r) =>
                        this.convertSitemapItem2Route(r, page)
                    )
                )
                .concat(basicRoutes);

            // 5. Configuración HOME
            const _home = pages.find(
                (p) =>
                    p.home === 1 &&
                    (p.name?.toLowerCase() === 'home' ||
                        p.name?.toLowerCase() === '_home')
            );

            if (_home) {
                const loadComp = () => this.getComponent(_home.template);

                _home.route?.forEach((r) => {
                    const fullPath = r.url
                        .replace(/^\/|\/$/g, '')
                        .toLowerCase();

                    pagesMaped.unshift({
                        path: fullPath,
                        loadComponent: loadComp,
                        pathMatch: 'full', // Importante para evitar coincidencias parciales
                    });

                    const langPrefix = fullPath.split('/')[0];
                    if (langPrefix && langPrefix !== fullPath) {
                        pagesMaped.unshift({
                            path: langPrefix,
                            redirectTo: fullPath,
                            pathMatch: 'full',
                        });
                    }
                });
            }

            // 6. Aplicar configuración
            this.router.config = pagesMaped;

            // --- CAMBIO CLAVE AQUÍ ---
            // Usamos la URL REAL del navegador, no la del Router
            const currentRealUrl = this.getCurrentPath();
            const currentClean = currentRealUrl
                .replace(/^\/|\/$/g, '')
                .toLowerCase();

            // 7. Forzado Manual: SOLO SI ESTAMOS EN LA RAÍZ PURA
            if (currentClean === '') {
                const finalDestination = rootRedirect.startsWith('/')
                    ? rootRedirect
                    : `/${rootRedirect}`;
                this.router.navigateByUrl(finalDestination, {
                    replaceUrl: true,
                });
            }

            // 8. Chequeo Global
            // Ya no necesitamos setTimeout para leer la URL, la tenemos en currentClean
            // Pero mantenemos un pequeño delay por seguridad del ciclo de vida de Angular
            setTimeout(() => {
                // Volvemos a leer por si acaso hubo una redirección interna muy rápida,
                // pero preferiblemente usamos la lógica segura.
                const checkUrl = this.getCurrentPath()
                    .split('?')[0]
                    .split('#')[0]
                    .replace(/^\/|\/$/g, '')
                    .toLowerCase();

                // Si la URL no está vacía, verificamos si coincide EXACTAMENTE con una página global
                if (checkUrl !== '') {
                    const pageGlobal = pages.find((page) =>
                        page?.route?.find((route: PageRoute) => {
                            if (!route.url || !route.global) return false;
                            const routeUrlClean = route.url
                                .split('?')[0]
                                .split('#')[0]
                                .replace(/^\/|\/$/g, '')
                                .toLowerCase();

                            return checkUrl === routeUrlClean;
                        })
                    );

                    // Si encontramos página global Y coincide la URL, forzamos (por si acaso el router no lo pilló)
                    // Pero normalmente el router ya habrá hecho su trabajo.
                    if (pageGlobal) {
                        // Lógica de seguridad para no redirigir en bucle
                        // Solo llamamos a checkRedirection si NO estamos ya donde queremos estar
                        // (Aunque con la comprobación de arriba === ya es seguro)
                        // this.checkRedirection(pageGlobal);
                    }
                }
            }, 100);
        }
    }

    private getCurrentPath(): string {
        if (isPlatformBrowser(this.platformId)) {
            return window.location.pathname;
        } else {
            return this.ssrUrl || '/';
        }
    }

    checkRedirection(page: PageFullUrls): void {
        this.router.navigateByUrl(
            page?.route?.find(
                (route) =>
                    route.languageId ===
                    this.siteSvc.selectedLanguageInstant?.id
            )!.url
        );
    }

    checkRedirectionTo404(): void {
        const notFoundRoute = this.siteSvc.webConfigInstant?.pages?.find(
            (page) => page.template?.toLowerCase() === '404'
        );

        if (notFoundRoute) {
            const url = notFoundRoute.route?.find(
                (r) => r.languageId === this.siteSvc.selectedLanguageInstant?.id
            )?.url;

            if (url) {
                this.router.navigateByUrl(url);
                return;
            }
        }

        this.router.navigateByUrl('/404');
    }

    private convertSitemapItem2Route(
        route: PageRoute,
        page: PageFullUrls
    ): Route[] {
        const fullPath = this.toPath(route.url);

        const mainRoute: Route = {
            path: fullPath,
            loadComponent: () => this.getComponent(page.template),
        };

        const aliasPath = fullPath.replace(/^([a-z]{2}(?:-[a-z]{2})?)\/?/, '');
        if (aliasPath === '') {
            return [
                {
                    path: '',
                    redirectTo: this.siteSvc.selectedLanguageInstant?.locate,
                    pathMatch: 'full',
                },
                mainRoute,
            ];
        }
        return [mainRoute];
    }

    private checkCurrentRouteParams(): string {
        let mainRoute = this.router.url;
        if (mainRoute.includes('#')) {
            mainRoute = mainRoute.split('#')[0];
        } else if (mainRoute.includes('?')) {
            mainRoute = mainRoute.split('?')[0];
        }
        // Elimina solo la barra inicial si existe
        if (mainRoute.startsWith('/')) {
            mainRoute = mainRoute.slice(1);
        }
        return mainRoute;
    }

    getPageId(): string | number | undefined {
        // Si estamos en SSR, devolvemos la home
        /*  if (isPlatformServer(this.platformId)) {
            return this.homeId;
        } */

        this.modelId = 0;
        let mainRoute = this.checkCurrentRouteParams();

        if (mainRoute === '') {
            return this.homeId;
        }

        // Normaliza la ruta eliminando / inicial y final
        const normalizedMainRoute = mainRoute
            .replace(/^\/|\/$/g, '')
            .toLowerCase();

        return this.getPageIdHome(normalizedMainRoute);
    }

    getPageIdHome(normalizedMainRoute: string): string | number | undefined {
        if (
            this.getIdHomeByRouteConfiguration() &&
            this.siteSvc.webConfigInstant?.pages?.find((page) => {
                return page?.route?.find((route) => {
                    const normalizedRouteUrl = route?.url
                        .replace(/^\/|\/$/g, '')
                        .toLowerCase();
                    return normalizedMainRoute === normalizedRouteUrl;
                });
            })?.pageId === undefined
        ) {
            return this.siteSvc.webConfigInstant?.pages?.find((page) => {
                return (
                    page?.home === 1 &&
                    (page.name?.toLowerCase() === 'home' ||
                        page.name?.toLowerCase() === '_home')
                );
            })?.pageId;
        } else {
            return this.siteSvc.webConfigInstant?.pages?.find((page) => {
                return page?.route?.find((route) => {
                    const normalizedRouteUrl = route?.url
                        .replace(/^\/|\/$/g, '')
                        .toLowerCase();
                    return normalizedMainRoute === normalizedRouteUrl;
                });
            })?.pageId;
        }
    }

    getIdHomeByRouteConfiguration() {
        let mainRoute = this.checkCurrentRouteParams();
        const normalizedMainRoute = mainRoute
            .replace(/^\/|\/$/g, '')
            .toLowerCase();
        return this.router.config?.find((page) => {
            return (
                page?.path?.replace(/^\/|\/$/g, '').toLowerCase() ===
                normalizedMainRoute
            );
        });
    }

    getModelId(): number | undefined {
        let mainRoute = this.checkCurrentRouteParams();
        let isItem = false;
        const normalizedMainRoute = mainRoute
            .replace(/^\/|\/$/g, '')
            .toLowerCase();

        const route =
            this.siteSvc.webConfigInstant?.pages?.find((page) =>
                page?.route?.find((route) => {
                    const normalizedRouteUrl = route?.url
                        .replace(/^\/|\/$/g, '')
                        .toLowerCase();
                    return normalizedMainRoute === normalizedRouteUrl;
                })
            ) ??
            this.siteSvc.webConfigInstant?.pages?.find((page) => {
                if (
                    page?.type?.toLowerCase() === 'hotel' ||
                    page?.template?.toLowerCase() === 'hotel'
                ) {
                    isItem = true;
                }
                return page?.home === 1;
            });

        if (this.modelId !== route?.modelId && route?.modelId) {
            this.modelId = route?.modelId;
        } else if (route?.home === 1 && isItem) {
            return route?.pageId ?? 1;
        }

        return this.modelId || 0;
    }

    getPageIdByTemplate(template: string): number | string | undefined {
        this.modelId = 0;

        return this.siteSvc.webConfigInstant?.pages?.find(
            (page) => page.template?.toLowerCase() === template?.toLowerCase()
        )?.pageId;
    }

    switchUrlDiferentLanguage(templateUrl: string): string | undefined {
        // 1) Separar PATH de query/hash
        const [rawPath, rawQueryHash = ''] = templateUrl.split(/(?=[?#])/);
        // rawQueryHash mantiene "?a=1" o "#x" o "?a=1#x"

        // 2) Normalizar SOLO el path
        const normalizedTemplatePath = (rawPath ?? '')
            .replace(/^\/|\/$/g, '')
            .toLowerCase();

        // Si está en la raíz o solo tiene el código de idioma, buscar la home
        const isRootOrLangOnly =
            !normalizedTemplatePath || normalizedTemplatePath.length === 2;

        const page = isRootOrLangOnly
            ? this.siteSvc.pages?.find((page) => page.home === 1)
            : (this.siteSvc.pages?.find((page) =>
                  page.route?.some((route) => {
                      const routePath = (route.url ?? '')
                          .split(/[?#]/)[0]
                          .replace(/^\/|\/$/g, '')
                          .toLowerCase();

                      return routePath === normalizedTemplatePath;
                  })
              ) ?? this.siteSvc.pages?.find((page) => page.home === 1));

        const newBaseUrl = page?.route.find(
            (route) =>
                Number(route.languageId) ===
                Number(this.siteSvc.selectedLanguageInstant?.id)
        )?.url;

        if (!newBaseUrl) return undefined;

        // 3) Reaplicar params/hash tal cual/eliminamos los parametros
        return `${newBaseUrl}`;
    }

    getHomeUrlByLanguage(languageId?: string | number): string {
        const langId = Number(
            languageId ?? this.siteSvc.selectedLanguageInstant?.id
        );

        const homePage =
            this.siteSvc.pages?.find(
                (p) =>
                    p?.home === 1 &&
                    (p?.name?.toLowerCase() === 'home' ||
                        p?.name?.toLowerCase() === '_home')
            ) ??
            this.siteSvc.webConfigInstant?.pages?.find(
                (p) =>
                    p?.home === 1 &&
                    (p?.name?.toLowerCase() === 'home' ||
                        p?.name?.toLowerCase() === '_home')
            );

        const url = homePage?.route?.find(
            (r) => Number(r.languageId) === langId
        )?.url;

        return url
            ? this.toAbs(url)
            : `/${this.siteSvc.selectedLanguageInstant?.locate ?? ''}`;
    }

    getLangPrefixByHome(languageId?: string | number): string {
        const homeUrl = this.getHomeUrlByLanguage(languageId);
        const lang = (homeUrl ?? '')
            .replace(/^\/|\/$/g, '')
            .split('/')[0]
            .toLowerCase();

        return lang ? `/${lang}` : '/';
    }

    filterPagesAndRoutes(
        pages: PageFullUrls[],
        concatUrl?: string
    ): PageFullUrls[] {
        return pages
            .filter(
                (page: PageFullUrls): page is PageFullUrls =>
                    page !== null && page.id !== null
            )
            .map((page: PageFullUrls) => {
                page.route = page.route
                    ?.filter((route) => route.url)
                    .map((route) => ({
                        ...route,
                        url: `${concatUrl || ''}${route.url}`,
                    }));
                return page;
            });
    }

    getComponent(template: string) {
        switch (template.toLowerCase()) {
            case TEMPLATES.GENERAL.toLowerCase():
                return import(
                    '../../pages/dynamic-page/dynamic-page.component'
                ).then((m) => m.DynamicPageComponent);

            case TEMPLATES.DYNAMIC.toLowerCase():
                return import(
                    '../../pages/dynamic-page/dynamic-page.component'
                ).then((m) => m.DynamicPageComponent);

            case TEMPLATES.RENTINGS.toLowerCase():
                return import(
                    '../../pages/dynamic-page/dynamic-page.component'
                ).then((m) => m.DynamicPageComponent);

            case TEMPLATES.HOTEL.toLowerCase():
                return import(
                    '../../pages/hotel-page/hotel-page.component'
                ).then((m) => m.HotelPageComponent);

            case TEMPLATES.WEBMAP.toLowerCase():
                return import('../../pages/webmap/webmap.component').then(
                    (m) => m.WebmapComponent
                );

            case TEMPLATES.FAQ.toLowerCase():
                return import('../../pages/faq/faq.component').then(
                    (m) => m.FaqComponent
                );

            case TEMPLATES.CONTACT.toLowerCase():
                return import('../../pages/contact/contact.component').then(
                    (m) => m.ContactComponent
                );

            case TEMPLATES.GALLERY.toLowerCase():
                return import('../../pages/gallery/gallery.component').then(
                    (m) => m.GalleryComponent
                );

            case TEMPLATES.THANKS.toLowerCase():
                return import('../../pages/thanks/thanks.component').then(
                    (m) => m.ThanksComponent
                );

            case TEMPLATES.LEGAL.toLowerCase():
                return import('../../pages/legal/legal.component').then(
                    (m) => m.LegalComponent
                );

            case TEMPLATES.ROOMS.toLowerCase():
                return import(
                    '../../pages/rooms-list/rooms-list.component'
                ).then((m) => m.RoomsListComponent);

            case TEMPLATES.ROOM_DETAIL.toLowerCase():
                return import(
                    '../../pages/room-details/room-details.component'
                ).then((m) => m.RoomDetailsComponent);

            case TEMPLATES.ROOMS2.toLowerCase():
                return import(
                    '../../pages/rooms-list-v2/rooms-list-v2.component'
                ).then((m) => m.RoomsListV2Component);

            case TEMPLATES.PROMO_DETAIL.toLowerCase():
                return import(
                    '../../pages/offer-detail/offer-detail.component'
                ).then((m) => m.OfferDetailComponent);

            case TEMPLATES.LIST_PROMOS.toLowerCase():
                return import(
                    '../../pages/offers-list/offers-list.component'
                ).then((m) => m.OffersListComponent);

            case TEMPLATES.LIST_PROMOS_V2.toLowerCase():
                return import(
                    '../../pages/offers-list-v2/offers-list-v2.component'
                ).then((m) => m.OffersListV2Component);

            case TEMPLATES.COMINGSOON.toLowerCase():
                return import(
                    '../../pages/coming-soon/coming-soon.component'
                ).then((m) => m.ComingSoonComponent);

            case TEMPLATES.HOTELS.toLowerCase():
                return import(
                    '../../pages/hotels-list/hotels-list.component'
                ).then((m) => m.HotelsListComponent);

            case TEMPLATES.LOCATION.toLowerCase():
                return import('../../pages/location/location.component').then(
                    (m) => m.LocationComponent
                );

            case TEMPLATES.BLOG_LIST.toLowerCase():
                return import('../../pages/blog/blog.component').then(
                    (m) => m.BlogComponent
                );

            case TEMPLATES.BLOG_DETAIL.toLowerCase():
                return import(
                    '../../pages/blog-detail/blog-detail.component'
                ).then((m) => m.BlogDetailComponent);

            case TEMPLATES.LOYALTY.toLowerCase():
                return import('../../pages/loyalty/loyalty.component').then(
                    (m) => m.LoyaltyComponent
                );

            default:
                return import('../../pages/404/404.component').then(
                    (m) => m.ErrorComponent
                );
        }
    }
}
